import * as THREE from 'three';
import {store} from 'store/customer';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';
import {getFloorClearanceSceneUnits} from 'components/customer/RoomPlanner/helpers/placeholder/floorClearance';
import {getCabinetLikeWorldOrigin} from 'components/customer/RoomPlanner/helpers/placeholder/getCabinetLikeWorldOrigin';
import {syncOverlayTransformToCabinet} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {refreshDistanceLines} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';

const ADD_DISTANCE_FROM_CABINET = 25;
const HALF_PI = Math.PI / 2;

/**
 * Overlay height in scene units — same rules as the layout hook so keyboard
 * nudges do not overwrite product-type heights with a raw bounding box.
 * @param {CabinetMesh} cabinetGroup - Selected cabinet or placeholder
 * @param {number} bboxHeight - Height from getGroupWidth
 * @param {number} scale - mm → scene scale
 * @return {number} Height to pass into dimension / wall-arrow onDrag
 */
export const getCabinetOverlayHeight = (
    cabinetGroup: CabinetMesh,
    bboxHeight: number,
    scale: number
): number => {
    if (cabinetGroup.userData?.isPlaceholder) {
        return bboxHeight;
    }

    const isManualPanel = Boolean(cabinetGroup.userData.isManualPanel);
    const shouldAddToeKick =
        Boolean(cabinetGroup.userData.isPanelProduct) ||
        Boolean(cabinetGroup.userData.isFillerProduct) ||
        Boolean(cabinetGroup.userData.isRobeProduct) ||
        Boolean(cabinetGroup.userData.isVerminProduct);

    if (isManualPanel) {
        const templateName = cabinetGroup.userData.templateName;
        const isExteriorPanel = Boolean(
            typeof templateName === 'string' &&
            templateName.includes('Exterior')
        );
        const values = cabinetGroup.userData.values as {
            cabinet_ext_thickness?: number | string;
            cabinet_carc_thickness?: number | string;
        };
        const thickness = isExteriorPanel
            ? Number(values.cabinet_ext_thickness)
            : Number(values.cabinet_carc_thickness);
        return thickness * scale;
    }

    if (cabinetGroup.userData.isLShapePanel || shouldAddToeKick) {
        return bboxHeight;
    }

    const values = cabinetGroup.userData.values as {
        type_name?: string;
        cabinet_panel_length?: number | string;
        cabinet_height?: number | string;
    };

    if (values?.type_name === 'Drawer Face') {
        return Number(values.cabinet_panel_length) * scale;
    }

    return Number(values?.cabinet_height) * scale;
};

const invokeWallArrowOnDrag = (
    arrow: THREE.Object3D,
    cabinetGroup: CabinetMesh,
    width: number,
    depth: number,
    height?: number,
    rebuildGeometry = false
): void => {
    // Floor-distance text is shared across items. Always rebuild it so a
    // base placeholder does not keep the previous cabinet's clearance.
    const isFloorDistance = arrow.userData?.id === 'arrowGroupLeft-distance';
    if (!rebuildGeometry && !isFloorDistance) return;
    const onDrag = (
        arrow.userData as {
            onDrag?: (w: number, d: number, g: THREE.Group, h?: number) => void;
        }
    ).onDrag;
    if (typeof onDrag !== 'function') return;
    if (height !== undefined) {
        onDrag(width, depth, cabinetGroup, height);
    } else {
        onDrag(width, depth, cabinetGroup);
    }
};

/**
 * Moves wall-parented dimension arrows (height, floor width/depth, clearance)
 * with the selected mesh. Same position math as useRoomLayout — no React effect.
 * @param {THREE.Scene} scene - Room scene
 * @param {CabinetMesh} cabinetGroup - Selected cabinet or placeholder
 * @param {number} width - Overlay width in scene units
 * @param {number} depth - Overlay depth in scene units
 * @param {number} height - Overlay height in scene units
 * @param {boolean} rebuildGeometry - Rebuild arrow/text (throttled)
 */
export const syncWallHeightMeasurementArrows = (
    scene: THREE.Scene,
    cabinetGroup: CabinetMesh,
    width: number,
    depth: number,
    height: number,
    rebuildGeometry = false
): void => {
    const {roomType, rectangularDimension, lShapedDimension} =
        store.getState().roomPlanner;
    const scale = 0.2;
    const isRectangular = roomType === 'RECTANGULAR';
    const currentDirection = String(
        cabinetGroup.userData.currentDirection ?? 'front'
    );

    const roomLength =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.length
            : lShapedDimension.leftWidth;
    const roomWidth =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.width
            : lShapedDimension.rightWidth;
    const dimensionHeight =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.height
            : lShapedDimension.height;

    let effectiveDepth2Wall = 0;
    let effectiveWidth2Wall = 0;
    if (currentDirection === 'left') {
        effectiveWidth2Wall = width;
    } else if (currentDirection === 'back') {
        effectiveDepth2Wall = -depth;
        effectiveWidth2Wall = width;
    } else if (currentDirection === 'right') {
        effectiveDepth2Wall = -depth;
    }

    const halfRoomWidth = (roomWidth * scale) / 2;
    const halfRoomLength = (roomLength * scale) / 2;
    const halfDimensionHeight = (dimensionHeight * scale) / 2;
    const origin = getCabinetLikeWorldOrigin(cabinetGroup);
    const baseHeight = origin.y + halfDimensionHeight;
    const heightArrowLocalY = baseHeight - 50;
    const heightArrowLocalYSide = baseHeight - 70;
    const baseCabinetX = origin.x;
    const baseCabinetZ = origin.z;

    const negCabinetX = -baseCabinetX;
    const negCabinetZ = -baseCabinetZ;
    const floorArrowHeightOffset = cabinetGroup.userData.isLShapePanel ? 0 : 15;
    const heightOffset = getFloorClearanceSceneUnits(
        cabinetGroup,
        dimensionHeight * scale
    );

    scene.traverse((arrow) => {
        const arrowId = arrow.userData?.id as string | undefined;
        if (
            arrowId !== 'arrowGroupLeft' &&
            arrowId !== 'arrowGroupLeft-distance' &&
            arrowId !== 'arrowGroupFront' &&
            arrowId !== 'arrowGroupBack' &&
            arrowId !== 'arrowGroupRight' &&
            arrowId !== 'arrowGroupTop'
        ) {
            return;
        }

        const resetRotation = () => {
            arrow.rotation.x = 0;
            arrow.rotation.y = 0;
            arrow.rotation.z = 0;
        };

        const dragIfNeeded = (
            target: THREE.Object3D,
            w: number,
            d: number,
            h?: number
        ): void => {
            invokeWallArrowOnDrag(
                target,
                cabinetGroup,
                w,
                d,
                h,
                rebuildGeometry
            );
        };

        if (arrowId === 'arrowGroupTop') {
            arrow.position.z = isRectangular ? -50 : 0;
            arrow.position.x = isRectangular
                ? negCabinetX + halfRoomWidth + 335
                : baseCabinetX + (lShapedDimension.rightWidth * scale) / 2;
            return;
        }

        if (arrowId === 'arrowGroupBack') {
            resetRotation();
            arrow.position.z = -50;
            arrow.position.y = 10;
            if (isRectangular) {
                arrow.position.x =
                    negCabinetX +
                    halfRoomWidth -
                    width +
                    20 +
                    effectiveWidth2Wall;
            } else {
                const baseX = negCabinetX + halfRoomWidth;
                if (currentDirection === 'left') {
                    arrow.position.x = baseX + 40;
                } else if (currentDirection === 'right') {
                    arrow.position.x =
                        baseX - effectiveWidth2Wall / 2 - width + 47;
                } else if (currentDirection === 'back') {
                    arrow.position.x = baseX + 50;
                } else {
                    arrow.position.x = baseX - effectiveWidth2Wall;
                }
            }
            dragIfNeeded(arrow, width, depth);
            return;
        }

        if (arrowId === 'arrowGroupFront') {
            if (isRectangular) {
                const isLeftOrRight =
                    currentDirection === 'left' || currentDirection === 'right';
                if (isLeftOrRight) {
                    arrow.position.z =
                        negCabinetZ +
                        halfRoomLength -
                        10 +
                        effectiveDepth2Wall +
                        ADD_DISTANCE_FROM_CABINET;
                    arrow.position.x =
                        negCabinetX +
                        halfRoomWidth +
                        (currentDirection === 'left' ? width - 10 : 0);
                    arrow.position.y = baseHeight + height - 40;
                    arrow.rotation.x = -HALF_PI;
                    arrow.rotation.y = HALF_PI;
                    dragIfNeeded(
                        arrow,
                        width,
                        depth,
                        height - floorArrowHeightOffset
                    );
                } else {
                    resetRotation();
                    arrow.position.z = -10;
                    arrow.position.y = 10;
                    arrow.position.x =
                        negCabinetX +
                        halfRoomWidth -
                        width +
                        20 +
                        effectiveWidth2Wall;
                    dragIfNeeded(arrow, width, depth);
                }
            } else {
                const isLeftOrRight =
                    currentDirection === 'left' || currentDirection === 'right';
                if (isLeftOrRight) {
                    arrow.position.z =
                        baseCabinetZ -
                        halfRoomLength +
                        (currentDirection === 'left'
                            ? 20 + effectiveDepth2Wall
                            : -effectiveDepth2Wall + 20);
                    arrow.position.x =
                        baseCabinetX +
                        halfRoomWidth -
                        (currentDirection === 'left'
                            ? width + ADD_DISTANCE_FROM_CABINET + 25
                            : 55);
                    arrow.position.y = baseHeight + height - 40;
                    arrow.rotation.x = -HALF_PI;
                    arrow.rotation.y = HALF_PI;
                    dragIfNeeded(arrow, width, depth, height - 15);
                } else {
                    resetRotation();
                    arrow.position.z = -10;
                    arrow.position.y = 10;
                    arrow.position.x = baseCabinetX + halfRoomWidth - 25;
                    dragIfNeeded(arrow, width, depth);
                }
            }
            return;
        }

        if (arrowId === 'arrowGroupRight') {
            arrow.position.z = isRectangular ? -45 : 0;
            arrow.position.x = isRectangular
                ? negCabinetZ + halfRoomLength + 20 + effectiveDepth2Wall
                : baseCabinetZ +
                  halfRoomLength -
                  effectiveDepth2Wall -
                  depth +
                  22;
            dragIfNeeded(arrow, width, depth);
            return;
        }

        if (arrowId === 'arrowGroupLeft') {
            if (isRectangular) {
                const isLeftOrRight =
                    currentDirection === 'left' || currentDirection === 'right';
                if (isLeftOrRight) {
                    arrow.position.y = heightArrowLocalYSide;
                    arrow.position.x =
                        negCabinetZ + halfRoomLength - 10 + effectiveDepth2Wall;
                    arrow.position.z = 10;
                    arrow.rotation.z = 0;
                    dragIfNeeded(arrow, width, depth);
                } else if (currentDirection === 'back') {
                    arrow.position.y = heightArrowLocalY;
                    arrow.position.x = negCabinetZ + halfRoomLength;
                    arrow.position.z =
                        baseCabinetX +
                        halfRoomWidth -
                        20 -
                        ADD_DISTANCE_FROM_CABINET;
                    arrow.rotation.z = HALF_PI;
                    dragIfNeeded(arrow, width, depth, height);
                } else {
                    arrow.position.y = heightArrowLocalY;
                    arrow.position.x = negCabinetZ + halfRoomLength - 10;
                    arrow.position.z =
                        baseCabinetX +
                        halfRoomWidth -
                        80 +
                        ADD_DISTANCE_FROM_CABINET;
                    arrow.rotation.z = HALF_PI;
                    dragIfNeeded(arrow, width, depth, height);
                }
            } else {
                const isLeftOrRight =
                    currentDirection === 'left' || currentDirection === 'right';
                if (isLeftOrRight) {
                    arrow.position.y = heightArrowLocalYSide;
                    arrow.position.x =
                        negCabinetZ + halfRoomLength - 30 + effectiveDepth2Wall;
                    arrow.position.z = 10;
                    arrow.rotation.z = 0;
                    dragIfNeeded(arrow, depth, width);
                } else if (currentDirection === 'back') {
                    arrow.position.y = baseHeight - 60;
                    arrow.position.x = negCabinetZ + halfRoomLength;
                    arrow.position.z =
                        baseCabinetX +
                        halfRoomWidth -
                        20 -
                        ADD_DISTANCE_FROM_CABINET;
                    arrow.rotation.z = HALF_PI;
                    dragIfNeeded(arrow, width, depth, height - 15);
                } else {
                    arrow.position.y = baseHeight - 65;
                    arrow.position.x =
                        negCabinetZ +
                        halfRoomLength +
                        20 -
                        ADD_DISTANCE_FROM_CABINET;
                    arrow.position.z =
                        baseCabinetX +
                        halfRoomWidth -
                        80 +
                        ADD_DISTANCE_FROM_CABINET;
                    arrow.rotation.z = HALF_PI;
                    dragIfNeeded(arrow, width, depth, height - 10);
                }
            }
            return;
        }

        arrow.rotation.z = HALF_PI;
        if (isRectangular) {
            const isLeftOrRight =
                currentDirection === 'left' || currentDirection === 'right';
            if (isLeftOrRight) {
                arrow.position.z =
                    baseCabinetX +
                    halfRoomWidth -
                    (currentDirection === 'left' ? width + 45 : 55);
                arrow.position.x =
                    negCabinetZ +
                    halfRoomLength +
                    (currentDirection === 'right' ? effectiveDepth2Wall : 0) -
                    35 +
                    ADD_DISTANCE_FROM_CABINET;
            } else if (currentDirection === 'back') {
                arrow.position.y = 0;
                arrow.position.x =
                    negCabinetZ +
                    halfRoomLength -
                    25 +
                    ADD_DISTANCE_FROM_CABINET;
                arrow.position.z =
                    baseCabinetX +
                    halfRoomWidth -
                    20 -
                    ADD_DISTANCE_FROM_CABINET;
            } else {
                arrow.position.y = 0;
                arrow.position.x = negCabinetZ + halfRoomLength - 10;
                arrow.position.z =
                    baseCabinetX +
                    halfRoomWidth -
                    80 +
                    ADD_DISTANCE_FROM_CABINET;
            }
        } else {
            resetRotation();
            arrow.rotation.z = HALF_PI;
            const isLeftOrRight =
                currentDirection === 'left' || currentDirection === 'right';
            if (isLeftOrRight) {
                arrow.position.z =
                    baseCabinetX +
                    halfRoomWidth +
                    (currentDirection === 'left' ? -width - 50 : 25);
                arrow.position.x =
                    negCabinetZ +
                    halfRoomLength +
                    effectiveDepth2Wall -
                    (currentDirection === 'left'
                        ? 35 - ADD_DISTANCE_FROM_CABINET
                        : 20);
                if (currentDirection === 'right') {
                    arrow.rotation.y = -Math.PI;
                }
            } else if (currentDirection === 'back') {
                arrow.position.z =
                    baseCabinetX +
                    halfRoomWidth -
                    20 -
                    ADD_DISTANCE_FROM_CABINET;
                arrow.position.x = negCabinetZ + halfRoomLength;
            } else {
                arrow.position.y = 0;
                arrow.position.x =
                    negCabinetZ +
                    halfRoomLength +
                    20 -
                    ADD_DISTANCE_FROM_CABINET;
                arrow.position.z =
                    baseCabinetX +
                    halfRoomWidth -
                    80 +
                    ADD_DISTANCE_FROM_CABINET;
            }
        }
        dragIfNeeded(arrow, width, depth, heightOffset);
    });
};

/**
 * Follows the selected mesh the same way the layout hook does for a normal
 * cabinet: copy pose, then onDrag(width, depth, height) with the left/right
 * swap. Cheap enough to run on every keyboard step.
 * @param {THREE.Object3D} overlay - cabinetDimensionArrows group
 * @param {CabinetMesh} cabinetGroup - Selected mesh
 * @param {number} width - Scene-unit width
 * @param {number} depth - Scene-unit depth
 * @param {number} height - Scene-unit overlay height
 * @param {boolean} rebuildGeometry - Rebuild dimension geometry (throttled)
 */
export const syncCabinetDimensionOverlay = (
    overlay: THREE.Object3D,
    cabinetGroup: CabinetMesh,
    width: number,
    depth: number,
    height: number,
    rebuildGeometry = true
): void => {
    syncOverlayTransformToCabinet(overlay as THREE.Group, cabinetGroup);
    if (!rebuildGeometry) return;

    const onDrag = (
        overlay.userData as {
            onDrag?: (w: number, d: number, h: number) => void;
        }
    ).onDrag;
    if (typeof onDrag !== 'function') return;

    const currentDirection = String(
        cabinetGroup.userData.currentDirection ?? 'front'
    );
    const isHorizontal =
        currentDirection === 'left' || currentDirection === 'right';
    if (isHorizontal) {
        onDrag(depth, width, height);
    } else {
        onDrag(width, depth, height);
    }
};

/**
 * Width/depth/height from getGroupWidth plus layout-hook overlay height.
 * @param {CabinetMesh} cabinetGroup - Selected mesh
 * @return {{width: number, depth: number, height: number}} Overlay size
 */
export const getCabinetOverlaySize = (
    cabinetGroup: CabinetMesh
): {width: number; depth: number; height: number} => {
    const {width, depth, height: bboxHeight} = getGroupWidth(cabinetGroup);
    const scale = 0.2;
    return {
        width,
        depth,
        height: getCabinetOverlayHeight(cabinetGroup, bboxHeight, scale),
    };
};

export const isScene = (
    object: THREE.Object3D | null | undefined
): object is THREE.Scene => object instanceof THREE.Scene;

/**
 * Same overlay follow path as the layout hook / keyboard nudges: dimension
 * labels, wall-distance guides, height arrows, then distance lines.
 * @param {CabinetMesh} cabinetGroup - Selected cabinet or placeholder
 * @param {THREE.Scene} scene - Room scene
 * @param {boolean} rebuildGeometry - Rebuild expensive overlay geometry
 */
export const syncOverlaysOnCabinetMove = (
    cabinetGroup: CabinetMesh,
    scene: THREE.Scene,
    rebuildGeometry = true
): void => {
    cabinetGroup.updateMatrixWorld(true);
    const selectedId = cabinetGroup.userData.id as string;
    const isDragging = store.getState().roomPlanner.isDragging;
    const shouldShowMovementOverlays =
        Boolean(cabinetGroup.userData.isMovementMode) && !isDragging;
    const {width, depth, height} = getCabinetOverlaySize(cabinetGroup);
    const currentDirection = String(
        cabinetGroup.userData.currentDirection ?? 'front'
    );
    const isHorizontal =
        currentDirection === 'left' || currentDirection === 'right';

    scene.traverse((child: THREE.Object3D) => {
        const groupId = child.userData?.id as string | undefined;
        const targetUuid = child.userData?.cabinetTargetUuid as
            string | undefined;
        const idMatches =
            groupId != null && String(groupId) === String(selectedId ?? '');
        const uuidMatches = targetUuid === cabinetGroup.uuid;
        if (!idMatches && !uuidMatches) return;

        if (
            child.name !== 'cabinetMovementArrows' &&
            child.name !== 'cabinetDimensionArrows' &&
            child.name !== 'distanceWallMeasurements' &&
            child.name !== 'distanceWallMeasurementsDashed'
        ) {
            return;
        }

        if (child.name === 'cabinetDimensionArrows') {
            syncCabinetDimensionOverlay(
                child,
                cabinetGroup,
                width,
                depth,
                height,
                rebuildGeometry
            );
            return;
        }

        const objectWithDrag = child.userData as {
            onDrag?: (w?: number, d?: number, h?: number) => void;
        };
        if (child.name === 'distanceWallMeasurementsDashed') {
            if (typeof objectWithDrag.onDrag === 'function') {
                objectWithDrag.onDrag();
            }
            return;
        }

        if (rebuildGeometry && typeof objectWithDrag.onDrag === 'function') {
            if (isHorizontal) {
                objectWithDrag.onDrag(depth, width, height);
            } else {
                objectWithDrag.onDrag(width, depth, height);
            }
        }

        syncOverlayTransformToCabinet(child as THREE.Group, cabinetGroup);

        if (
            child.name === 'cabinetMovementArrows' ||
            child.name === 'distanceWallMeasurements'
        ) {
            child.visible = shouldShowMovementOverlays;
            child.traverse((obj) => {
                obj.visible = shouldShowMovementOverlays;
            });
        }
    });

    syncWallHeightMeasurementArrows(
        scene,
        cabinetGroup,
        width,
        depth,
        height,
        rebuildGeometry
    );

    // The gap lines to nearby products stay off with the Guides switch
    // (CO-5856); with no choices made, guides show.
    const showGuides =
        store.getState().roomPlanner.snapSettings?.guides ?? true;
    if (rebuildGeometry && showGuides) {
        refreshDistanceLines(
            scene,
            cabinetGroup,
            productEvents.getLayoutMeshes()
        );
    }
};
